import React, { useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { alpha, Box, Button, CircularProgress, Collapse, Divider, InputAdornment, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../../../config";
import RayaField from "../../../../components/elements/fields/RayaField";
import CardBrandIcon from "../../../wallet/components/CardBrandIcon";
import SavedPaymentMethodRow from "../../../wallet/components/SavedPaymentMethodRow";
import { detectCardBrandFromPan, guessBankFromPan } from "../../../wallet/cardBrand";
import { paymentMethodAddErrorMessage, validateCardForStore } from "../../../wallet/cardValidation";

function CheckoutPaymentCardsPanel({ cards, cardsLoading, selectedId, onSelectId, setCards, loginToken, enqueueSnackbar }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showPicker, setShowPicker] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [defaultUpdatingId, setDefaultUpdatingId] = useState(null);
    const [comfortCvv, setComfortCvv] = useState("");
    const [addForm, setAddForm] = useState({
        holder_name: "",
        card_number: "",
        exp_month: "",
        exp_year: "",
        label: "",
    });

    const closePicker = useCallback(() => setShowPicker(false), []);

    const togglePicker = useCallback(() => {
        setShowAddForm(false);
        setShowPicker((v) => !v);
    }, []);

    const openAddForm = useCallback(() => {
        setShowPicker(false);
        setShowAddForm(true);
    }, []);

    const toggleAddForm = useCallback(() => {
        setShowPicker(false);
        setShowAddForm((v) => !v);
    }, []);

    useEffect(() => {
        if (!showAddForm) {
            setComfortCvv("");
        }
    }, [showAddForm]);

    const selected = useMemo(() => cards.find((c) => c.id === selectedId) || null, [cards, selectedId]);
    const otherCards = useMemo(() => cards.filter((c) => c.id !== selectedId), [cards, selectedId]);
    const canSwitchCard = otherCards.length > 0;

    const inputCardBrand = useMemo(() => detectCardBrandFromPan(addForm.card_number), [addForm.card_number]);
    const inputBankGuess = useMemo(() => guessBankFromPan(addForm.card_number), [addForm.card_number]);

    const authJsonHeaders = useCallback(
        () => ({
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        }),
        [loginToken],
    );

    const authJsonBodyHeaders = useCallback(
        () => ({
            ...authJsonHeaders(),
            "Content-Type": "application/json",
        }),
        [authJsonHeaders],
    );

    const removeCard = useCallback(
        async (id) => {
            try {
                await axios.delete(`${defaultVariables.url}/payment-methods/${id}`, { headers: authJsonHeaders() });
                enqueueSnackbar(t("paramPayCardRemoved"), { variant: "success" });
                setCards((prev) => prev.filter((c) => c.id !== id));
            } catch (e) {
                enqueueSnackbar(t("paramPayCardRemoveFailed"), { variant: "error" });
            }
        },
        [authJsonHeaders, enqueueSnackbar, setCards, t],
    );

    const setCardDefault = useCallback(
        async (id, isDefault) => {
            setDefaultUpdatingId(id);
            try {
                await axios.patch(
                    `${defaultVariables.url}/payment-methods/${id}`,
                    { is_default: isDefault },
                    { headers: authJsonBodyHeaders() },
                );
                setCards((prev) =>
                    isDefault
                        ? prev.map((c) => ({ ...c, is_default: c.id === id }))
                        : prev.map((c) => (c.id === id ? { ...c, is_default: false } : c)),
                );
                if (isDefault) {
                    onSelectId(id);
                }
            } catch (e) {
                enqueueSnackbar(t("paramPayCardDefaultFailed"), { variant: "error" });
            } finally {
                setDefaultUpdatingId(null);
            }
        },
        [authJsonBodyHeaders, enqueueSnackbar, onSelectId, setCards, t],
    );

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        const err = validateCardForStore(addForm);
        if (err) {
            enqueueSnackbar(t(`paramPayCardError_${err}`), { variant: "error" });
            return;
        }
        setAddLoading(true);
        try {
            const res = await axios.post(`${defaultVariables.url}/payment-methods`, addForm, {
                headers: authJsonBodyHeaders(),
            });
            const newCard = res.data;
            enqueueSnackbar(t("paramPayCardAdded"), { variant: "success" });
            setCards((prev) => [newCard, ...prev.filter((c) => c.id !== newCard.id)]);
            if (newCard?.id != null) {
                onSelectId(newCard.id);
            }
            setShowAddForm(false);
            setShowPicker(false);
            setAddForm({
                holder_name: "",
                card_number: "",
                exp_month: "",
                exp_year: "",
                label: "",
            });
            setComfortCvv("");
        } catch (err) {
            enqueueSnackbar(paymentMethodAddErrorMessage(err, t), { variant: "error" });
        } finally {
            setAddLoading(false);
        }
    };

    const rootSx = { mt: 1.5 };

    const addCardForm = (boxSx = {}) => (
        <Box
            component="form"
            onSubmit={handleAddSubmit}
            dir="ltr"
            sx={{ direction: "ltr", textAlign: "left", mb: 2, ...boxSx }}
        >
            <Typography variant="subtitle2" gutterBottom sx={{ textAlign: "inherit" }}>
                {t("paramPayAddCard")}
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <RayaField
                        variant="outlined"
                        required
                        fullWidth
                        name="holder_name"
                        label={t("paramPayHolder")}
                        value={addForm.holder_name}
                        onChange={(e) => setAddForm({ ...addForm, holder_name: e.target.value })}
                        autoComplete="cc-name"
                        slotProps={{
                            htmlInput: { dir: "ltr", style: { textAlign: "left" } },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <RayaField
                        variant="outlined"
                        required
                        fullWidth
                        name="card_number"
                        label={t("paramPayCardNumber")}
                        value={addForm.card_number}
                        onChange={(e) => setAddForm({ ...addForm, card_number: e.target.value })}
                        autoComplete="cc-number"
                        slotProps={{
                            htmlInput: {
                                dir: "ltr",
                                style: { textAlign: "left" },
                                inputMode: "numeric",
                            },
                            input: {
                                endAdornment: inputCardBrand ? (
                                    <InputAdornment
                                        position="end"
                                        sx={{
                                            ml: 1,
                                            mr: 0.25,
                                            alignSelf: "center",
                                            maxHeight: "none",
                                        }}
                                    >
                                        <CardBrandIcon brand={inputCardBrand} size="large" />
                                    </InputAdornment>
                                ) : undefined,
                            },
                        }}
                    />
                    {inputBankGuess ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, ml: 0.5 }}>
                            {t("paramPayBankGuessPrefix")} {inputBankGuess}
                        </Typography>
                    ) : null}
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <RayaField
                        variant="outlined"
                        required
                        fullWidth
                        name="exp_month"
                        label={t("paramPayExpMonth")}
                        value={addForm.exp_month}
                        onChange={(e) =>
                            setAddForm({ ...addForm, exp_month: e.target.value.replace(/\D/g, "").slice(0, 2) })
                        }
                        autoComplete="cc-exp-month"
                        slotProps={{
                            htmlInput: {
                                dir: "ltr",
                                style: { textAlign: "left" },
                                inputMode: "numeric",
                                maxLength: 2,
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <RayaField
                        variant="outlined"
                        required
                        fullWidth
                        name="exp_year"
                        label={t("paramPayExpYear")}
                        value={addForm.exp_year}
                        onChange={(e) =>
                            setAddForm({ ...addForm, exp_year: e.target.value.replace(/\D/g, "").slice(0, 4) })
                        }
                        autoComplete="cc-exp-year"
                        slotProps={{
                            htmlInput: {
                                dir: "ltr",
                                style: { textAlign: "left" },
                                inputMode: "numeric",
                                maxLength: 4,
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                    <RayaField
                        variant="outlined"
                        fullWidth
                        name="cvv2_comfort_only"
                        label={t("paramPayCvv2")}
                        type="password"
                        value={comfortCvv}
                        onChange={(e) => setComfortCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        autoComplete="off"
                        slotProps={{
                            htmlInput: {
                                dir: "ltr",
                                style: { textAlign: "left" },
                                inputMode: "numeric",
                                maxLength: 4,
                                autoComplete: "off",
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <RayaField
                        variant="outlined"
                        optional
                        fullWidth
                        name="label"
                        label={t("paramPayCardLabel")}
                        value={addForm.label}
                        onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                        slotProps={{
                            htmlInput: { dir: "ltr", style: { textAlign: "left" } },
                        }}
                    />
                </Grid>
            </Grid>
            <Box display="flex" flexDirection="row" gap={1} mt={2} sx={{ justifyContent: "flex-start" }}>
                <Button type="submit" variant="contained" disabled={addLoading}>
                    {addLoading ? <CircularProgress size={20} /> : t("save")}
                </Button>
                <Button type="button" variant="outlined" onClick={() => setShowAddForm(false)}>
                    {t("cancelTooltip")}
                </Button>
            </Box>
        </Box>
    );

    if (cardsLoading) {
        return (
            <Box sx={{ ...rootSx, display: "flex", alignItems: "center", py: 1.5 }}>
                <CircularProgress size={22} />
            </Box>
        );
    }

    return (
        <Box sx={rootSx}>
            {cards.length === 0 && !showAddForm ? (
                <>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
                        {t("checkoutNoSavedCards")}
                    </Typography>
                    <Button fullWidth variant="contained" color="primary" sx={{ mb: 2 }} onClick={openAddForm}>
                        {t("paramPayAddCard")}
                    </Button>
                </>
            ) : null}

            {cards.length === 0 && showAddForm ? addCardForm() : null}

            {cards.length > 0 && selected ? (
                <>
                    <SavedPaymentMethodRow
                        method={selected}
                        onRemoveCard={removeCard}
                        onDefaultChange={setCardDefault}
                        defaultUpdatingId={defaultUpdatingId}
                    />

                    <Stack direction="row" spacing={1} sx={{ mb: showAddForm ? 0 : 1, mt: 0, flexWrap: "wrap" }} useFlexGap>
                        {canSwitchCard ? (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="medium"
                                onClick={togglePicker}
                                sx={{ textTransform: "none", flex: "1 1 140px" }}
                            >
                                {t("checkoutChangeCard")}
                            </Button>
                        ) : null}
                        <Button
                            variant={showAddForm ? "contained" : "outlined"}
                            color="primary"
                            size="medium"
                            onClick={toggleAddForm}
                            sx={{ textTransform: "none", flex: "1 1 140px" }}
                        >
                            {t("checkoutAddCard")}
                        </Button>
                    </Stack>

                    {showAddForm ? addCardForm({ mt: 1.5, mb: 1 }) : null}

                    <Collapse in={showPicker && canSwitchCard} timeout="auto" unmountOnExit>
                        <Box sx={{ mb: 1 }}>
                            <Divider sx={{ mb: 1 }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                                {t("checkoutPickAnotherCard")}
                            </Typography>
                            {otherCards.map((c) => (
                                <SavedPaymentMethodRow
                                    key={c.id}
                                    method={c}
                                    onRowClick={() => {
                                        onSelectId(c.id);
                                        closePicker();
                                    }}
                                    onRemoveCard={removeCard}
                                    onDefaultChange={setCardDefault}
                                    defaultUpdatingId={defaultUpdatingId}
                                    cardSx={{
                                        transition: theme.transitions.create(["border-color", "background-color"], {
                                            duration: theme.transitions.duration.shorter,
                                        }),
                                        "&:hover": {
                                            borderColor: alpha(theme.palette.primary.main, 0.45),
                                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                                        },
                                        "&:last-of-type": { mb: 0 },
                                    }}
                                    cardContentSx={{ cursor: "pointer" }}
                                />
                            ))}
                        </Box>
                    </Collapse>
                </>
            ) : null}
        </Box>
    );
}

export default CheckoutPaymentCardsPanel;
